import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Trash2, Eye, EyeOff, ExternalLink, RefreshCw, BarChart3, FileText, Globe, ArrowLeft, Activity, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_COLORS = {
  manual: { bg: 'bg-blue-500/20', text: 'text-blue-300', label: 'Manual' },
  outrank: { bg: 'bg-emerald-500/20', text: 'text-emerald-300', label: 'Outrank' },
  opinly: { bg: 'bg-amber-500/20', text: 'text-amber-300', label: 'Opinly' },
  backlinkmanagement: { bg: 'bg-rose-500/20', text: 'text-rose-300', label: 'BacklinkMgmt' },
  static_migration: { bg: 'bg-gray-500/20', text: 'text-gray-300', label: 'Seed' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white/[0.04] border border-white/10 rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm text-gray-400">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

const BlogAdmin = () => {
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [activeTab, setActiveTab] = useState('posts');
  const [seoHealth, setSeoHealth] = useState(null);
  const [seoLoading, setSeoLoading] = useState(false);
  const [seoSort, setSeoSort] = useState('score-asc');
  const [indexnow, setIndexnow] = useState(null);
  const [linkRunning, setLinkRunning] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [submittingAll, setSubmittingAll] = useState(false);
  const [submitAllResult, setSubmitAllResult] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/admin/stats`);
      const data = await res.json();
      setStats(data);
    } catch {}
  }, []);

  const fetchSeoHealth = useCallback(async () => {
    setSeoLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/admin/seo-health`);
      const data = await res.json();
      setSeoHealth(data);
    } catch {}
    setSeoLoading(false);
  }, []);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, limit: '200' });
      if (search) params.set('search', search);
      if (sourceFilter) params.set('source', sourceFilter);
      const res = await fetch(`${API_URL}/api/blog/posts?${params}`);
      const data = await res.json();
      setPosts(data.posts || []);
    } catch {}
    setLoading(false);
  }, [search, statusFilter, sourceFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchPosts(); }, [fetchPosts]);
  const fetchIndexnow = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/indexnow/history?limit=8`);
      setIndexnow(await res.json());
    } catch {}
  }, []);
  useEffect(() => { if (activeTab === 'seo') { fetchSeoHealth(); fetchIndexnow(); } }, [activeTab, fetchSeoHealth, fetchIndexnow]);

  const handleRunLinkInjection = async () => {
    setLinkRunning(true); setLinkResult(null);
    try {
      const res = await fetch(`${API_URL}/api/blog/admin/internal-links/run`, { method: 'POST' });
      setLinkResult(await res.json());
      fetchSeoHealth();
    } catch {}
    setLinkRunning(false);
  };

  const handleSubmitAllIndexnow = async () => {
    setSubmittingAll(true); setSubmitAllResult(null);
    try {
      const res = await fetch(`${API_URL}/api/indexnow/submit-all`, { method: 'POST' });
      setSubmitAllResult(await res.json());
      fetchIndexnow();
    } catch {}
    setSubmittingAll(false);
  };

  const handlePublish = async (slug) => {
    setActionLoading(slug);
    try {
      await fetch(`${API_URL}/api/blog/posts/${slug}/publish`, { method: 'PATCH' });
      fetchPosts();
      fetchStats();
    } catch {}
    setActionLoading('');
  };

  const handleUnpublish = async (slug) => {
    setActionLoading(slug);
    try {
      await fetch(`${API_URL}/api/blog/posts/${slug}/unpublish`, { method: 'PATCH' });
      fetchPosts();
      fetchStats();
    } catch {}
    setActionLoading('');
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    setActionLoading(slug);
    try {
      await fetch(`${API_URL}/api/blog/posts/${slug}`, { method: 'DELETE' });
      fetchPosts();
      fetchStats();
    } catch {}
    setActionLoading('');
  };

  const handleOpinlySync = async () => {
    setSyncing(true);
    try {
      await fetch(`${API_URL}/api/integrations/opinly/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${process.env.REACT_APP_OPINLY_KEY || ''}` }
      });
      fetchPosts();
      fetchStats();
    } catch {}
    setSyncing(false);
  };

  const srcInfo = (source) => SOURCE_COLORS[source] || SOURCE_COLORS.manual;

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="blog-admin-dashboard">
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-30 bg-[#0a0a0f]/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors" data-testid="admin-back-link">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-lg font-bold text-white">Blog Admin</h1>
              <p className="text-xs text-gray-500">Manage all content sources</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleOpinlySync}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-300 text-sm font-medium rounded-lg hover:bg-amber-500/30 transition-colors disabled:opacity-50"
              data-testid="admin-opinly-sync-btn"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Opinly'}
            </button>
            <Link
              to="/blog"
              className="flex items-center gap-2 px-4 py-2 bg-white/5 text-gray-300 text-sm font-medium rounded-lg hover:bg-white/10 transition-colors"
              data-testid="admin-view-blog-link"
            >
              <Globe className="w-4 h-4" />
              View Blog
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-1 mb-8 p-1 bg-white/[0.03] rounded-xl w-fit border border-white/10" data-testid="admin-tabs">
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-5 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'posts' ? 'bg-purple-500/20 text-purple-300' : 'text-gray-400 hover:text-white'}`}
            data-testid="admin-tab-posts"
          >
            <FileText className="w-4 h-4 inline mr-2" />Posts
          </button>
          <button
            onClick={() => setActiveTab('seo')}
            className={`px-5 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'seo' ? 'bg-emerald-500/20 text-emerald-300' : 'text-gray-400 hover:text-white'}`}
            data-testid="admin-tab-seo"
          >
            <Activity className="w-4 h-4 inline mr-2" />SEO Health
          </button>
        </div>

        {activeTab === 'seo' ? (
          /* ══════ SEO HEALTH DASHBOARD ══════ */
          <div data-testid="seo-health-dashboard">
            {seoLoading ? (
              <div className="text-center py-16 text-gray-500">Analyzing SEO health...</div>
            ) : seoHealth ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="seo-summary">
                  <div className="bg-white/[0.04] border border-white/10 rounded-xl p-5">
                    <p className="text-xs text-gray-500 mb-1">Avg SEO Score</p>
                    <p className={`text-3xl font-bold ${seoHealth.summary.avg_score >= 80 ? 'text-emerald-400' : seoHealth.summary.avg_score >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                      {seoHealth.summary.avg_score}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">out of 100</p>
                  </div>
                  <div className="bg-white/[0.04] border border-white/10 rounded-xl p-5">
                    <p className="text-xs text-gray-500 mb-1">Grade A Posts</p>
                    <p className="text-3xl font-bold text-emerald-400">{seoHealth.summary.grade_distribution.good}</p>
                    <p className="text-xs text-gray-600 mt-1">score 80+</p>
                  </div>
                  <div className="bg-white/[0.04] border border-white/10 rounded-xl p-5">
                    <p className="text-xs text-gray-500 mb-1">Need Attention</p>
                    <p className="text-3xl font-bold text-amber-400">{seoHealth.summary.grade_distribution.warning}</p>
                    <p className="text-xs text-gray-600 mt-1">score 60-79</p>
                  </div>
                  <div className="bg-white/[0.04] border border-white/10 rounded-xl p-5">
                    <p className="text-xs text-gray-500 mb-1">Critical</p>
                    <p className="text-3xl font-bold text-red-400">{seoHealth.summary.grade_distribution.critical}</p>
                    <p className="text-xs text-gray-600 mt-1">score &lt;60</p>
                  </div>
                  <div className="bg-white/[0.04] border border-white/10 rounded-xl p-5">
                    <p className="text-xs text-gray-500 mb-1">Missing Heroes</p>
                    <p className="text-3xl font-bold text-orange-400">{seoHealth.summary.posts_missing_hero}</p>
                    <p className="text-xs text-gray-600 mt-1">of {seoHealth.summary.total}</p>
                  </div>
                </div>

                {/* SEO Ops — Internal Linking Engine + IndexNow */}
                <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="seo-ops-panel">
                  {/* Internal Linking Engine */}
                  <div className="bg-white/[0.04] border border-white/10 rounded-xl p-5" data-testid="link-engine-card">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-semibold text-white">Internal Linking Engine</p>
                        <p className="text-xs text-gray-500 mt-0.5">Auto-inject contextual cross-links into orphan posts</p>
                      </div>
                      <button
                        onClick={handleRunLinkInjection}
                        disabled={linkRunning}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-300 text-xs font-medium rounded-lg hover:bg-purple-500/30 transition-colors disabled:opacity-50"
                        data-testid="run-link-engine-btn"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${linkRunning ? 'animate-spin' : ''}`} />
                        {linkRunning ? 'Weaving...' : 'Run Engine'}
                      </button>
                    </div>
                    {linkResult && (
                      <div className="text-xs text-gray-400 bg-white/[0.03] rounded-lg p-3" data-testid="link-engine-result">
                        Scanned <span className="text-white font-semibold">{linkResult.posts_scanned}</span> posts ·
                        Found <span className="text-amber-300 font-semibold"> {linkResult.orphans_found}</span> orphans ·
                        Injected <span className="text-emerald-300 font-semibold"> {linkResult.links_injected}</span> links into
                        <span className="text-white font-semibold"> {linkResult.posts_updated}</span> posts
                      </div>
                    )}
                  </div>

                  {/* IndexNow */}
                  <div className="bg-white/[0.04] border border-white/10 rounded-xl p-5" data-testid="indexnow-card">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-semibold text-white">IndexNow (Bing/Yandex)</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {indexnow?.stats?.total_pings || 0} pings · {indexnow?.stats?.successful || 0} OK
                          {indexnow?.stats?.last_ping ? ` · last ${formatDate(indexnow.stats.last_ping)}` : ''}
                          {' '}· auto-fires on every publish
                        </p>
                      </div>
                      <button
                        onClick={handleSubmitAllIndexnow}
                        disabled={submittingAll}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-300 text-xs font-medium rounded-lg hover:bg-emerald-500/30 transition-colors disabled:opacity-50"
                        data-testid="indexnow-submit-all-btn"
                      >
                        <Globe className={`w-3.5 h-3.5 ${submittingAll ? 'animate-pulse' : ''}`} />
                        {submittingAll ? 'Submitting...' : 'Submit All URLs'}
                      </button>
                    </div>
                    {submitAllResult && (
                      <div className="text-xs text-gray-400 bg-white/[0.03] rounded-lg p-3 mb-2" data-testid="indexnow-submit-result">
                        {submitAllResult.success ? <CheckCircle className="w-3.5 h-3.5 inline text-emerald-400 mr-1" /> : <XCircle className="w-3.5 h-3.5 inline text-red-400 mr-1" />}
                        Submitted <span className="text-white font-semibold">{submitAllResult.urls_submitted}</span> URLs — status {submitAllResult.status_code}
                      </div>
                    )}
                    {indexnow?.pings?.length > 0 && (
                      <div className="space-y-1 max-h-28 overflow-y-auto" data-testid="indexnow-history">
                        {indexnow.pings.map((p) => (
                          <div key={p.ping_id} className="flex items-center justify-between text-xs text-gray-500">
                            <span className="flex items-center gap-1.5">
                              {p.success ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
                              {p.source} · {p.url_count} URL{p.url_count !== 1 ? 's' : ''}
                            </span>
                            <span>{formatDate(p.created_at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Sort Controls */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs text-gray-500">Sort by:</span>
                  {[
                    { key: 'score-asc', label: 'Worst First' },
                    { key: 'score-desc', label: 'Best First' },
                    { key: 'words-asc', label: 'Shortest' },
                    { key: 'links-asc', label: 'Fewest Links' },
                  ].map(s => (
                    <button
                      key={s.key}
                      onClick={() => setSeoSort(s.key)}
                      className={`px-3 py-1 text-xs rounded-full transition-all ${seoSort === s.key ? 'bg-purple-500/20 text-purple-300' : 'bg-white/5 text-gray-400 hover:text-white'}`}
                      data-testid={`seo-sort-${s.key}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>

                {/* Posts SEO Table */}
                <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden" data-testid="seo-health-table">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10 text-left">
                          <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Post</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-20 text-center">Score</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-16 text-center">Title</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-16 text-center">Meta</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-20 text-center">Words</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-16 text-center">Links</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-16 text-center">H2s</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-16 text-center">Hero</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-48">Issues</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {[...seoHealth.posts].sort((a, b) => {
                          const [field, dir] = seoSort.split('-');
                          const mult = dir === 'asc' ? 1 : -1;
                          if (field === 'score') return (a.score - b.score) * mult;
                          if (field === 'words') return (a.word_count - b.word_count) * mult;
                          if (field === 'links') return (a.internal_links - b.internal_links) * mult;
                          return 0;
                        }).map(p => (
                          <tr key={p.slug} className="hover:bg-white/[0.02] transition-colors" data-testid={`seo-row-${p.slug}`}>
                            <td className="px-4 py-3">
                              <Link to={`/blog/${p.slug}`} className="text-sm text-gray-200 hover:text-white transition-colors line-clamp-1">
                                {p.title}
                              </Link>
                              <span className="text-xs text-gray-600 block mt-0.5">{p.published_at}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg text-sm font-bold ${
                                p.grade === 'A' ? 'bg-emerald-500/15 text-emerald-400' :
                                p.grade === 'B' ? 'bg-amber-500/15 text-amber-400' :
                                'bg-red-500/15 text-red-400'
                              }`}>
                                {p.score}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-xs ${p.title_length <= 60 ? 'text-emerald-400' : 'text-red-400'}`}>{p.title_length}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-xs ${p.meta_length >= 120 ? 'text-emerald-400' : 'text-red-400'}`}>{p.meta_length}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-xs ${p.word_count >= 1500 ? 'text-emerald-400' : p.word_count >= 800 ? 'text-amber-400' : 'text-red-400'}`}>
                                {p.word_count.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-xs ${p.internal_links >= 3 ? 'text-emerald-400' : p.internal_links >= 1 ? 'text-amber-400' : 'text-red-400'}`}>
                                {p.internal_links}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-xs ${p.h2_count >= 4 ? 'text-emerald-400' : 'text-amber-400'}`}>{p.h2_count}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {p.has_hero ? <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" /> : <XCircle className="w-4 h-4 text-red-400 mx-auto" />}
                            </td>
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                {p.issues.slice(0, 2).map((issue, i) => (
                                  <div key={i} className={`text-xs flex items-start gap-1 ${issue.type === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                                    <span className="line-clamp-1">{issue.msg}</span>
                                  </div>
                                ))}
                                {p.issues.length === 0 && <span className="text-xs text-emerald-400">No issues</span>}
                                {p.issues.length > 2 && <span className="text-xs text-gray-500">+{p.issues.length - 2} more</span>}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="border-t border-white/10 px-5 py-3 text-xs text-gray-500">
                    {seoHealth.posts.length} posts analyzed | Avg words: {seoHealth.summary.avg_word_count} | Avg links: {seoHealth.summary.avg_internal_links}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        ) : (
        /* ══════ POSTS TAB (original content) ══════ */
        <div>
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-grid">
            <StatCard icon={FileText} label="Total Posts" value={stats.total} accent="bg-purple-500/20 text-purple-300" />
            <StatCard icon={Eye} label="Published" value={stats.published} accent="bg-emerald-500/20 text-emerald-300" />
            <StatCard icon={EyeOff} label="Drafts" value={stats.drafts} accent="bg-amber-500/20 text-amber-300" />
            <StatCard icon={BarChart3} label="Total Views" value={stats.total_views} accent="bg-cyan-500/20 text-cyan-300" />
          </div>
        )}

        {/* Source Breakdown */}
        {stats?.sources && (
          <div className="flex flex-wrap gap-3 mb-8" data-testid="admin-source-breakdown">
            {Object.entries(stats.sources).map(([src, count]) => {
              const info = srcInfo(src);
              return (
                <button
                  key={src}
                  onClick={() => setSourceFilter(sourceFilter === src ? '' : src)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    sourceFilter === src ? 'ring-2 ring-white/30 ' + info.bg + ' ' + info.text : info.bg + ' ' + info.text + ' opacity-70 hover:opacity-100'
                  }`}
                  data-testid={`admin-filter-source-${src}`}
                >
                  {info.label}: {count}
                </button>
              );
            })}
          </div>
        )}

        {/* Top Posts */}
        {stats?.top_posts?.length > 0 && (
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5 mb-8" data-testid="admin-top-posts">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Top Posts by Views</h3>
            <div className="space-y-2">
              {stats.top_posts.map((p, i) => (
                <div key={p.slug} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs text-gray-500 w-5 text-right">{i + 1}.</span>
                    <Link to={`/blog/${p.slug}`} className="text-sm text-gray-300 hover:text-white truncate transition-colors">
                      {p.title}
                    </Link>
                  </div>
                  <div className="flex items-center gap-2 ml-3 shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${srcInfo(p.source).bg} ${srcInfo(p.source).text}`}>
                      {srcInfo(p.source).label}
                    </span>
                    <span className="text-sm font-mono text-cyan-300">{p.views}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters & Search */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="admin-filters">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search posts by title, description, or tags..."
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'published', 'draft'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                  statusFilter === s
                    ? 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/40'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
                data-testid={`admin-filter-status-${s}`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Posts Table */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden" data-testid="admin-posts-table">
          {loading ? (
            <div className="text-center py-16 text-gray-500">Loading posts...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16 text-gray-500">No posts found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 text-left">
                    <th className="px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Title</th>
                    <th className="px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-24">Source</th>
                    <th className="px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-24">Status</th>
                    <th className="px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-20 text-right">Views</th>
                    <th className="px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-28">Date</th>
                    <th className="px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-32 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {posts.map((post) => {
                    const info = srcInfo(post.source);
                    const isLoading = actionLoading === post.slug;
                    return (
                      <tr key={post.slug} className="hover:bg-white/[0.02] transition-colors" data-testid={`admin-row-${post.slug}`}>
                        <td className="px-5 py-3">
                          <Link to={`/blog/${post.slug}`} className="text-sm text-gray-200 hover:text-white transition-colors line-clamp-1">
                            {post.title}
                          </Link>
                          {post.category && <span className="text-xs text-gray-500 mt-0.5 block">{post.category}</span>}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${info.bg} ${info.text}`}>
                            {info.label}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`text-xs font-medium ${post.status === 'published' ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {post.status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="text-sm font-mono text-gray-300">{post.views || 0}</span>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-xs text-gray-500">{formatDate(post.published_at || post.created_at)}</span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Link
                              to={`/blog/${post.slug}`}
                              className="p-1.5 text-gray-500 hover:text-white rounded-md hover:bg-white/10 transition-all"
                              title="View post"
                              data-testid={`admin-view-${post.slug}`}
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Link>
                            {post.status === 'published' ? (
                              <button
                                onClick={() => handleUnpublish(post.slug)}
                                disabled={isLoading}
                                className="p-1.5 text-gray-500 hover:text-amber-400 rounded-md hover:bg-amber-500/10 transition-all disabled:opacity-50"
                                title="Unpublish"
                                data-testid={`admin-unpublish-${post.slug}`}
                              >
                                <EyeOff className="w-3.5 h-3.5" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handlePublish(post.slug)}
                                disabled={isLoading}
                                className="p-1.5 text-gray-500 hover:text-emerald-400 rounded-md hover:bg-emerald-500/10 transition-all disabled:opacity-50"
                                title="Publish"
                                data-testid={`admin-publish-${post.slug}`}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(post.slug)}
                              disabled={isLoading}
                              className="p-1.5 text-gray-500 hover:text-red-400 rounded-md hover:bg-red-500/10 transition-all disabled:opacity-50"
                              title="Delete"
                              data-testid={`admin-delete-${post.slug}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="border-t border-white/10 px-5 py-3 text-xs text-gray-500">
            Showing {posts.length} post{posts.length !== 1 ? 's' : ''}
          </div>
        </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default BlogAdmin;
